import React, { useCallback, useEffect, useState } from "react";
import {
    Box, Checkbox, Chip, FormControlLabel, IconButton, InputAdornment,
    Paper, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow,
    TextField, Tooltip, Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import CreditScoreRoundedIcon from "@mui/icons-material/CreditScoreRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";

import FormModal from "../../../../component/common/FormModal";
import ResizableTable from "../../../../component/common/ResizableTable";
import ActionButton from "../../../../component/common/ActionButton";
import FormField from "../../../../component/common/FormField";
import LoadingDots from "../../../../component/common/LoadingDots";
import { financeApi, fmtKRWFull } from "../../api/financeApi";
import { ds } from "../../../../styles/designSystem";
import { formatDate, today as getToday } from "../../../../util/dateUtils";
import { formatNumberWithCommas, toNumericInputState } from "../../../../util/numberFormat";
import { runApiAction } from "../../../../util/apiFeedback";
import { confirm as confirmDialog } from "../../../../util/confirmDialog";
import { useMyPermissions } from "../../../../util/useMyPermissions";

// sortOrder 는 UI 에서 다루지 않는다 — 등록 시 맨 뒤에 붙고, 순서는 드래그로만 바꾼다(내부 값).
// openingBalance = 개시 잔액(seed) — 마감 이력 없을 때만 시작 잔액으로 쓰인다.
const EMPTY_ACCOUNT = { bankName: "", alias: "", accountNo: "", note: "", openingBalance: "", active: true };
const EMPTY_LOAN = {
    lender: "", title: "", balance: "", interestRate: "", paymentDay: "",
    maturityDate: "", note: "", sortOrder: 0, active: true,
};

const shiftDate = (dateStr, days) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return formatDate(d);
};

export default function DailyCashTab() {
    const { hasPerm } = useMyPermissions();
    const canManage = hasPerm("FINANCE_DAILYCASH");

    const [date, setDate]       = useState(formatDate(getToday()));
    const [report, setReport]   = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy]       = useState(false);

    // 마감 다이얼로그 (계좌별 보정값)
    const [closeOpen, setCloseOpen]     = useState(false);
    const [closeValues, setCloseValues] = useState({}); // accountId → display 문자열

    // 계좌 관리
    const [accountsOpen, setAccountsOpen] = useState(false);
    const [accounts, setAccounts]         = useState([]);
    const [accForm, setAccForm]           = useState(null);
    const [accEditing, setAccEditing]     = useState(null);
    const [accOpeningDisplay, setAccOpeningDisplay] = useState("");  // 개시 잔액 입력(콤마 표시)
    const [dragAccId, setDragAccId]       = useState(null);   // 메인 표에서 드래그 중인 계좌 id

    // 대출 관리
    const [loanOpen, setLoanOpen]       = useState(false);
    const [loanForm, setLoanForm]       = useState(EMPTY_LOAN);
    const [loanEditing, setLoanEditing] = useState(null);
    const [loanBalanceDisplay, setLoanBalanceDisplay] = useState("");

    // 이자 지출등록 모달 — 선택한 대출의 금리 기준 월 이자를 자동 계산해 채워두고, 확인 시 지출 예정 등록
    const [interestOpen, setInterestOpen]   = useState(false);
    const [interestItems, setInterestItems] = useState([]);  // [{loanId, lender, title, rate, balance, amountDisplay, scheduledDate}]

    const load = useCallback(() => {
        setLoading(true);
        return financeApi.getDailyCash(date)
            .then(setReport)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [date]);

    useEffect(() => { load(); }, [load]);

    const accountsInReport = report?.accounts || [];

    // 자금일보 입출금은 현금흐름표 '완료(확정)'·결재 지출확정 시 자동 반영된다 — 수동 입력 없음.

    // ─── 마감 ────────────────────────────────────────────────────

    const openClose = () => {
        const init = {};
        accountsInReport.forEach(ar => {
            init[ar.account.id] = formatNumberWithCommas(ar.computedClosing, { emptyZero: false });
        });
        setCloseValues(init);
        setCloseOpen(true);
    };

    const submitClose = async () => {
        const overrides = accountsInReport.map(ar => {
            const raw = String(closeValues[ar.account.id] ?? "").replace(/,/g, "");
            const val = raw === "" || raw === "-" ? ar.computedClosing : Number(raw);
            return { accountId: ar.account.id, closingBalance: val };
        });
        const result = await runApiAction({
            request: () => financeApi.closeDailyCash({ date, overrides }),
            successMsg: `${date} 일마감이 완료되었습니다.`,
            setLoading: setBusy,
            throwOnError: true,
        });
        if (result.ok) { setCloseOpen(false); load(); }
    };

    const reopen = async () => {
        if (!(await confirmDialog({ message: `${date} 마감을 해제하시겠습니까?` }))) return;
        await runApiAction({
            request: () => financeApi.reopenDailyCash(date),
            successMsg: "마감이 해제되었습니다.",
            setLoading: setBusy,
        });
        load();
    };

    // ─── 계좌 관리 ───────────────────────────────────────────────

    const loadAccounts = () => financeApi.getBankAccounts().then(setAccounts).catch(() => {});
    const openAccounts = () => { setAccForm(null); setAccEditing(null); loadAccounts(); setAccountsOpen(true); };

    const saveAccount = async () => {
        const payload = {
            ...accForm,
            openingBalance: accForm.openingBalance === "" || accForm.openingBalance == null
                ? null : Number(accForm.openingBalance),
        };
        const result = await runApiAction({
            request: () => accEditing
                ? financeApi.updateBankAccount(accEditing.id, payload)
                : financeApi.createBankAccount(payload),
            successMsg: accEditing ? "계좌가 수정되었습니다." : "계좌가 등록되었습니다.",
            throwOnError: true,
        });
        if (result.ok) { setAccForm(null); setAccEditing(null); loadAccounts(); load(); }
    };

    const deactivateAccount = async (a) => {
        if (!(await confirmDialog({ message: `"${a.alias}" 계좌를 거래중지 처리하시겠습니까? (이력은 보존됩니다)` }))) return;
        await financeApi.deleteBankAccount(a.id);
        loadAccounts(); load();
    };

    // 드래그 정렬 — 자금일보 메인 표(계좌 보는 화면)에서 계좌 순서를 바꾸고 저장.
    // srcId 는 dataTransfer 로 받아 state 타이밍 문제 없이 확실하게 처리.
    const dropAccount = async (srcId, targetId) => {
        setDragAccId(null);
        const order = accountsInReport.map(ar => ar.account.id);
        const from = order.indexOf(srcId);
        const to   = order.indexOf(targetId);
        if (srcId == null || Number.isNaN(srcId) || from < 0 || to < 0 || from === to) return;
        const next = [...order];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        // 낙관적 반영 — report.accounts 를 새 순서로 재배열
        setReport(r => r
            ? { ...r, accounts: next.map(id => r.accounts.find(ar => ar.account.id === id)).filter(Boolean) }
            : r);
        await financeApi.reorderBankAccounts(next).catch(() => {});
        load();
    };

    // ─── 대출 관리 ───────────────────────────────────────────────

    const openLoanCreate = () => {
        setLoanEditing(null);
        setLoanForm(EMPTY_LOAN);
        setLoanBalanceDisplay("");
        setLoanOpen(true);
    };

    const openLoanEdit = (l) => {
        setLoanEditing(l);
        setLoanForm({
            lender: l.lender, title: l.title, balance: l.balance,
            interestRate: l.interestRate ?? "", paymentDay: l.paymentDay ?? "",
            maturityDate: l.maturityDate ?? "", note: l.note || "",
            sortOrder: l.sortOrder ?? 0, active: l.active,
        });
        setLoanBalanceDisplay(formatNumberWithCommas(l.balance, { emptyZero: true }));
        setLoanOpen(true);
    };

    const saveLoan = async () => {
        const payload = {
            ...loanForm,
            balance: Number(loanForm.balance),
            interestRate: loanForm.interestRate === "" ? null : Number(loanForm.interestRate),
            paymentDay: loanForm.paymentDay === "" ? null : Number(loanForm.paymentDay),
            maturityDate: loanForm.maturityDate || null,
            sortOrder: Number(loanForm.sortOrder) || 0,
        };
        const result = await runApiAction({
            request: () => loanEditing
                ? financeApi.updateLoan(loanEditing.id, payload)
                : financeApi.createLoan(payload),
            successMsg: loanEditing ? "대출 정보가 수정되었습니다." : "대출이 등록되었습니다.",
            throwOnError: true,
        });
        if (result.ok) { setLoanOpen(false); load(); }
    };

    // 납입일 기준 다음 지급 예정일 — paymentDay 없으면 오늘. 이번 달 납입일이 지났으면 다음 달로.
    const nextPaymentDate = (paymentDay) => {
        const now = new Date();
        if (!paymentDay) return formatDate(now);
        const y = now.getFullYear(), m = now.getMonth();
        const clamp = (yy, mm) => Math.min(paymentDay, new Date(yy, mm + 1, 0).getDate());
        const todayMid = new Date(y, m, now.getDate());
        let d = new Date(y, m, clamp(y, m));
        if (d < todayMid) d = new Date(y, m + 1, clamp(y, m + 1));
        return formatDate(d);
    };

    // 선택한 대출의 금리 기준 월 이자(잔액×연%÷12)를 자동 계산해 모달에 채워둔다. (계산만 자동, 등록은 모달에서 확인)
    const openInterestModal = () => {
        const items = (report?.loans || [])
            .filter(l => loanSelected.has(l.id) && l.interestRate > 0 && (l.balance || 0) > 0)
            .map(l => {
                const monthly = Math.round(l.balance * (l.interestRate / 100) / 12);
                return {
                    loanId: l.id, lender: l.lender, title: l.title,
                    rate: l.interestRate, balance: l.balance, paymentDay: l.paymentDay,
                    amountDisplay: formatNumberWithCommas(monthly, { emptyZero: true }),
                    scheduledDate: nextPaymentDate(l.paymentDay),   // 납입일 기준 자동 채움(이번 달 지났으면 다음 달)
                };
            });
        if (items.length === 0) {
            confirmDialog({ message: "선택한 대출에 연 이자율이 없어 이자를 계산할 수 없습니다.\n대출 행을 클릭해 이자율을 먼저 입력하세요." });
            return;
        }
        setInterestItems(items);
        setInterestOpen(true);
    };

    const setInterestField = (loanId, key, value) =>
        setInterestItems(items => items.map(it => it.loanId === loanId ? { ...it, [key]: value } : it));

    // 모달에서 '등록' — 각 대출 이자를 지출 '예정'으로 생성 (현금흐름표 반영)
    const submitInterest = async () => {
        const payloads = interestItems
            .map(it => ({ ...it, amount: Number(String(it.amountDisplay).replace(/,/g, "")) }))
            .filter(it => it.amount > 0 && it.scheduledDate);
        if (payloads.length === 0) return;
        const result = await runApiAction({
            request: () => Promise.all(payloads.map(it => financeApi.createCashPlan({
                flowType: "EXPENSE",
                title: `${it.title} 이자`,
                amount: it.amount,
                scheduledDate: it.scheduledDate,
                category: "이자",
                vendorName: it.lender,
                status: "SCHEDULED",
            }))),
            successMsg: `이자 ${payloads.length}건을 지출 예정으로 등록했습니다. (현금흐름표 반영)`,
            setLoading: setBusy,
            throwOnError: true,
        });
        if (result.ok) { setInterestOpen(false); setLoanSelected(new Set()); load(); }
    };

    // 대출 — 좌측 체크박스 선택 → 일괄 삭제
    const [loanSelected, setLoanSelected] = useState(() => new Set());

    const toggleLoanSelect = (id) => setLoanSelected(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const toggleLoanSelectAll = (list) => {
        const targets = list.map(l => l.id);
        setLoanSelected(prev => targets.length > 0 && targets.every(id => prev.has(id))
            ? new Set()
            : new Set(targets));
    };

    const handleLoanBulkDelete = async () => {
        if (loanSelected.size === 0) return;
        if (!(await confirmDialog({ message: `선택한 대출 ${loanSelected.size}건을 삭제하시겠습니까?\n(상환 완료·대환 종료는 삭제 대신 수정에서 '실행 중'을 꺼 주세요)` }))) return;
        await Promise.allSettled([...loanSelected].map(id => financeApi.deleteLoan(id)));
        setLoanSelected(new Set());
        load();
    };

    const handleAmountInput = (raw, setter, field, setDisplay) => {
        const { value, display } = toNumericInputState(raw, { emptyZero: true });
        setter(f => ({ ...f, [field]: value }));
        setDisplay(display);
    };

    // ─── 렌더 ────────────────────────────────────────────────────

    // 금일적요(내용) 열과 금액 열을 분리해 그린다 — 같은 순서로 매핑돼 줄이 세로로 정렬된다.
    const renderSummaries = (ar, direction) => {
        const list = ar.entries.filter(e => e.direction === direction);
        if (list.length === 0) return <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>—</Typography>;
        return (
            <Box>
                {list.map(e => (
                    <Typography key={e.id} noWrap title={e.summary}
                        sx={{ fontSize: 12, lineHeight: 1.3, py: 0.2 }}>
                        {e.summary || "—"}
                    </Typography>
                ))}
            </Box>
        );
    };
    const renderAmounts = (ar, direction) => {
        const list = ar.entries.filter(e => e.direction === direction);
        if (list.length === 0) return <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>—</Typography>;
        const color = direction === "INCOME" ? ds.finance.income : ds.finance.expense;
        return (
            <Box>
                {list.map(e => (
                    <Typography key={e.id}
                        sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3, py: 0.2, whiteSpace: "nowrap", textAlign: "right", color }}>
                        {fmtKRWFull(e.amount)}
                    </Typography>
                ))}
            </Box>
        );
    };

    return (
        <Box>
            {/* 날짜 네비게이션 + 액션 */}
            <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, alignItems: "center", flexWrap: "wrap" }}>
                <IconButton size="small" onClick={() => setDate(d => shiftDate(d, -1))}>
                    <ChevronLeftRoundedIcon />
                </IconButton>
                <TextField size="small" type="date" value={date}
                    onChange={e => e.target.value && setDate(e.target.value)}
                    sx={{ width: 160 }} />
                <IconButton size="small" onClick={() => setDate(d => shiftDate(d, 1))}>
                    <ChevronRightRoundedIcon />
                </IconButton>
                {report?.closed
                    ? <Chip icon={<LockRoundedIcon sx={{ fontSize: 14 }} />} label="마감 완료" size="small"
                        sx={{ bgcolor: "#f0fdf4", color: "#16a34a", fontWeight: 700 }} />
                    : <Chip icon={<LockOpenRoundedIcon sx={{ fontSize: 14 }} />} label="미마감" size="small"
                        sx={{ bgcolor: "#fffbeb", color: "#d97706", fontWeight: 700 }} />}
                <Box sx={{ flex: 1 }} />
                {canManage && <>
                    <ActionButton variant="secondary" onClick={openAccounts}
                        startIcon={<AccountBalanceRoundedIcon sx={{ fontSize: 15 }} />}
                        label="계좌 관리" sx={{ fontSize: 12 }} />
                    {report?.closed ? (
                        <ActionButton variant="secondary" onClick={reopen} loading={busy}
                            label="마감 해제" sx={{ fontSize: 12 }} />
                    ) : (
                        <ActionButton variant="add" onClick={openClose} loading={busy}
                            disabled={accountsInReport.length === 0}
                            startIcon={<LockRoundedIcon sx={{ fontSize: 15 }} />}
                            label="일마감" sx={{ fontSize: 12 }} />
                    )}
                </>}
            </Box>

            {/* KPI */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                <Paper sx={{ ...ds.cardPadded, flex: 1 }}>
                    <Typography sx={ds.typography.label}>전일 잔액 합계</Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#64748b", mt: 0.5 }}>
                        {fmtKRWFull(report?.totalOpening ?? 0)}
                    </Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1 }}>
                    <Typography sx={ds.typography.label}>금일 입금</Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: ds.finance.income, mt: 0.5 }}>
                        +{fmtKRWFull(report?.totalIn ?? 0)}
                    </Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1 }}>
                    <Typography sx={ds.typography.label}>금일 출금</Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: ds.finance.expense, mt: 0.5 }}>
                        −{fmtKRWFull(report?.totalOut ?? 0)}
                    </Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1 }}>
                    <Typography sx={ds.typography.label}>총 예금 잔액</Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f766e", mt: 0.5 }}>
                        {fmtKRWFull(report?.totalClosing ?? 0)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        {report?.closed ? "일마감 확정 잔액" : "실시간 계산 잔액"}
                    </Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1 }}>
                    <Typography sx={ds.typography.label}>대출 잔액 합계</Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#9333ea", mt: 0.5 }}>
                        {fmtKRWFull(report?.loanTotal ?? 0)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        {report?.loans?.length ?? 0}건 실행 중
                    </Typography>
                </Paper>
            </Box>

            {/* 계좌별 자금일보 */}
            <Paper sx={{ ...ds.card, mb: 3 }}>
                {loading && <LoadingDots />}
                {!loading && <ResizableTable size="small" sx={ds.tableSx}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 150 }}>계좌</TableCell>
                            <TableCell align="right" sx={{ width: 120 }}>전일잔고</TableCell>
                            <TableCell>금일적요</TableCell>
                            <TableCell align="right" sx={{ width: 120 }}>입금</TableCell>
                            <TableCell>금일적요</TableCell>
                            <TableCell align="right" sx={{ width: 120 }}>출금</TableCell>
                            <TableCell align="right" sx={{ width: 140 }}>잔액</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {accountsInReport.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                                    등록된 계좌가 없습니다. [계좌 관리]에서 은행 계좌를 먼저 등록하세요.
                                </TableCell>
                            </TableRow>
                        ) : <>
                            {accountsInReport.map(ar => {
                                const mismatch = ar.closed && ar.closedBalance !== ar.computedClosing;
                                return (
                                    <TableRow key={ar.account.id}
                                        draggable={canManage}
                                        onDragStart={canManage ? (e => {
                                            setDragAccId(ar.account.id);
                                            e.dataTransfer.effectAllowed = "move";
                                            e.dataTransfer.setData("text/plain", String(ar.account.id));
                                        }) : undefined}
                                        onDragOver={canManage ? (e => e.preventDefault()) : undefined}
                                        onDrop={canManage ? (e => {
                                            e.preventDefault();
                                            dropAccount(Number(e.dataTransfer.getData("text/plain")), ar.account.id);
                                        }) : undefined}
                                        onDragEnd={() => setDragAccId(null)}
                                        sx={{
                                            cursor: canManage ? "grab" : "default",
                                            ...(dragAccId === ar.account.id && { opacity: 0.4 }),
                                        }}>
                                        <TableCell sx={{ verticalAlign: "top" }}>
                                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                                                {canManage && (
                                                    <DragIndicatorRoundedIcon
                                                        sx={{ fontSize: 16, color: "#cbd5e1", mt: 0.2 }} />
                                                )}
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{ar.account.alias}</Typography>
                                                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                                                        {ar.account.bankName}{ar.account.accountNo ? ` · ${ar.account.accountNo}` : ""}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right" sx={{ verticalAlign: "top", whiteSpace: "nowrap", color: "#64748b" }}>
                                            {fmtKRWFull(ar.openingBalance)}
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: "top" }}>{renderSummaries(ar, "INCOME")}</TableCell>
                                        <TableCell align="right" sx={{ verticalAlign: "top" }}>{renderAmounts(ar, "INCOME")}</TableCell>
                                        <TableCell sx={{ verticalAlign: "top" }}>{renderSummaries(ar, "EXPENSE")}</TableCell>
                                        <TableCell align="right" sx={{ verticalAlign: "top" }}>{renderAmounts(ar, "EXPENSE")}</TableCell>
                                        <TableCell align="right" sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                                                {fmtKRWFull(ar.closed ? ar.closedBalance : ar.computedClosing)}
                                            </Typography>
                                            {mismatch && (
                                                <Tooltip title={`계산 잔액 ${fmtKRWFull(ar.computedClosing)} 과 차이가 있습니다 (통장 실잔액 보정됨)`}>
                                                    <Typography sx={{ fontSize: 10.5, color: "#d97706", fontWeight: 600 }}>
                                                        보정 {fmtKRWFull(ar.closedBalance - ar.computedClosing)}
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            <TableRow sx={{ bgcolor: "#f8fafc" }}>
                                <TableCell sx={{ fontWeight: 800 }}>합계</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>
                                    {fmtKRWFull(report?.totalOpening ?? 0)}
                                </TableCell>
                                <TableCell />
                                <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: "nowrap", color: ds.finance.income }}>
                                    {fmtKRWFull(report?.totalIn ?? 0)}
                                </TableCell>
                                <TableCell />
                                <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: "nowrap", color: ds.finance.expense }}>
                                    {fmtKRWFull(report?.totalOut ?? 0)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: "nowrap", color: "#0f766e" }}>
                                    {fmtKRWFull(report?.totalClosing ?? 0)}
                                </TableCell>
                            </TableRow>
                        </>}
                    </TableBody>
                </ResizableTable>}
            </Paper>

            {/* 대출 현황 */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>현재 실행 대출</Typography>
                <Box sx={{ flex: 1 }} />
                {canManage && loanSelected.size > 0 && (
                    <Box sx={{
                        display: "flex", alignItems: "center", gap: 1.5,
                        px: 2, py: 0.6
                    }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#334155" }}>
                            {loanSelected.size}건 선택됨
                        </Typography>
                        <ActionButton variant="add" onClick={openInterestModal}
                            label="이자 지출등록" sx={{ fontSize: 11 }} />
                        <ActionButton variant="cancel" onClick={handleLoanBulkDelete}
                            label="선택 삭제" sx={{ fontSize: 11, color: "#dc2626" }} />
                    </Box>
                )}
                {canManage && (
                    <ActionButton variant="secondary" onClick={openLoanCreate} label="대출 등록" sx={{ fontSize: 12 }} />
                )}
            </Box>
            <Paper sx={ds.card}>
                {!loading && <ResizableTable size="small" sx={ds.tableSx}>
                    <TableHead>
                        <TableRow>
                            {canManage && (
                                <TableCell padding="checkbox">
                                    <Checkbox size="small"
                                        checked={(report?.loans || []).length > 0
                                            && (report?.loans || []).every(l => loanSelected.has(l.id))}
                                        indeterminate={loanSelected.size > 0
                                            && !(report?.loans || []).every(l => loanSelected.has(l.id))}
                                        onChange={() => toggleLoanSelectAll(report?.loans || [])} />
                                </TableCell>
                            )}
                            <TableCell>대출 기관</TableCell>
                            <TableCell>대출명</TableCell>
                            <TableCell align="right">대출 잔액</TableCell>
                            <TableCell align="right">이자율</TableCell>
                            <TableCell>납입일</TableCell>
                            <TableCell>만기일</TableCell>
                            <TableCell>비고</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(report?.loans || []).length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={canManage ? 8 : 7} align="center" sx={{ py: 3, color: "#94a3b8" }}>
                                    등록된 대출이 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : <>
                            {report.loans.map(l => (
                                <TableRow key={l.id} hover={canManage}
                                    onClick={() => canManage && openLoanEdit(l)}
                                    sx={{ cursor: canManage ? "pointer" : "default" }}>
                                    {canManage && (
                                        <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                                            <Checkbox size="small" checked={loanSelected.has(l.id)}
                                                onChange={() => toggleLoanSelect(l.id)} />
                                        </TableCell>
                                    )}
                                    <TableCell>{l.lender}</TableCell>
                                    <TableCell>{l.title}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                                        {fmtKRWFull(l.balance)}
                                    </TableCell>
                                    <TableCell align="right">{l.interestRate != null ? `${l.interestRate}%` : "—"}</TableCell>
                                    <TableCell>{l.paymentDay ? `매월 ${l.paymentDay}일` : "—"}</TableCell>
                                    <TableCell>{l.maturityDate || "—"}</TableCell>
                                    <TableCell sx={{ fontSize: 12, color: "#64748b", maxWidth: 220 }}>
                                        <Typography noWrap sx={{ fontSize: 12 }} title={l.note}>{l.note || "—"}</Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                            <TableRow sx={{ bgcolor: "#f8fafc" }}>
                                {canManage && <TableCell padding="checkbox" />}
                                <TableCell sx={{ fontWeight: 800 }}>대출 합계</TableCell>
                                <TableCell />
                                <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: "nowrap", color: "#9333ea" }}>
                                    {fmtKRWFull(report?.loanTotal ?? 0)}
                                </TableCell>
                                <TableCell colSpan={4} />
                            </TableRow>
                        </>}
                    </TableBody>
                </ResizableTable>}
            </Paper>

            {/* 일마감 */}
            <FormModal open={closeOpen} onClose={() => setCloseOpen(false)}
                title={`${date} 일마감`} subtitle="통장 실잔액과 다르면 금액을 보정한 뒤 마감하세요."
                maxWidth="sm" onSave={submitClose} saving={busy} saveLabel="마감 확정">
                <Stack spacing={1.5}>
                    {accountsInReport.map(ar => (
                        <Box key={ar.account.id} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, width: 150, flexShrink: 0 }} noWrap>
                                {ar.account.alias}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#94a3b8", width: 120, flexShrink: 0 }}>
                                계산 {fmtKRWFull(ar.computedClosing)}
                            </Typography>
                            <TextField size="small" fullWidth
                                value={closeValues[ar.account.id] ?? ""}
                                onChange={e => {
                                    const { display } = toNumericInputState(e.target.value, { emptyZero: false });
                                    setCloseValues(v => ({ ...v, [ar.account.id]: display }));
                                }}
                                InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                                inputProps={{ inputMode: "numeric" }} />
                        </Box>
                    ))}
                </Stack>
            </FormModal>

            {/* 계좌 관리 */}
            <FormModal open={accountsOpen} onClose={() => setAccountsOpen(false)}
                title="계좌 관리" maxWidth="md" cancelLabel="닫기">
                {accForm === null ? (
                    <Box>

                        <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mb: 1 }}>
                            순서 변경은 자금일보 표에서 계좌를 드래그하세요. 현재 잔액은 최근 일마감 기준(읽기 전용)입니다.
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
                            <ActionButton variant="add" sx={{ fontSize: 12 }} label="계좌 추가"
                                          onClick={() => { setAccEditing(null); setAccForm(EMPTY_ACCOUNT); setAccOpeningDisplay(""); }} />
                        </Box>
                        <ResizableTable size="small" sx={ds.tableSx}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>표시명</TableCell>
                                    <TableCell>은행</TableCell>
                                    <TableCell>계좌번호</TableCell>
                                    <TableCell align="right">현재 잔액</TableCell>
                                    <TableCell>상태</TableCell>
                                    <TableCell align="center">관리</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {accounts.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 3, color: "#94a3b8" }}>
                                            등록된 계좌가 없습니다.
                                        </TableCell>
                                    </TableRow>
                                ) : accounts.map(a => (
                                    <TableRow key={a.id} sx={{ opacity: a.active ? 1 : 0.45 }}>
                                        <TableCell sx={{ fontWeight: 600 }}>{a.alias}</TableCell>
                                        <TableCell>{a.bankName}</TableCell>
                                        <TableCell>{a.accountNo || "—"}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                                            {fmtKRWFull(a.currentBalance ?? 0)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={a.active ? "사용 중" : "거래중지"} size="small" sx={{
                                                height: 20, fontSize: 11,
                                                bgcolor: a.active ? "#f0fdf4" : "#f1f5f9",
                                                color: a.active ? "#16a34a" : "#94a3b8",
                                            }} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                                <IconButton size="small" onClick={() => {
                                                    setAccEditing(a);
                                                    setAccForm({
                                                        bankName: a.bankName, alias: a.alias,
                                                        accountNo: a.accountNo || "", note: a.note || "",
                                                        openingBalance: a.openingBalance ?? "", active: a.active,
                                                    });
                                                    setAccOpeningDisplay(a.openingBalance != null
                                                        ? formatNumberWithCommas(a.openingBalance, { emptyZero: false }) : "");
                                                }}>
                                                    <EditIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                                {a.active && (
                                                    <IconButton size="small" color="error" onClick={() => deactivateAccount(a)}>
                                                        <DeleteIcon sx={{ fontSize: 15 }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </ResizableTable>
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                            {accEditing ? "계좌 수정" : "새 계좌"}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <FormField label="은행명" required>
                                <TextField size="small" value={accForm.bankName}
                                    onChange={e => setAccForm(f => ({ ...f, bankName: e.target.value }))}
                                    placeholder="예: 하나은행" />
                            </FormField>
                            <FormField label="표시명" required>
                                <TextField size="small" value={accForm.alias}
                                    onChange={e => setAccForm(f => ({ ...f, alias: e.target.value }))}
                                    placeholder="예: 하나_804" />
                            </FormField>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <FormField label="계좌번호">
                                <TextField size="small" value={accForm.accountNo}
                                    onChange={e => setAccForm(f => ({ ...f, accountNo: e.target.value }))} />
                            </FormField>
                            <FormField label="개시 잔액">
                                <TextField size="small" value={accOpeningDisplay}
                                    onChange={e => handleAmountInput(e.target.value, setAccForm, "openingBalance", setAccOpeningDisplay)}
                                    InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                                    inputProps={{ inputMode: "numeric" }} placeholder="0" />
                                <Typography sx={{ fontSize: 10.5, color: "#94a3b8", mt: 0.3 }}>
                                    일마감 이력이 없을 때만 시작 잔액으로 쓰입니다.
                                </Typography>
                            </FormField>
                        </Box>
                        <FormField label="비고">
                            <TextField size="small" value={accForm.note}
                                onChange={e => setAccForm(f => ({ ...f, note: e.target.value }))} />
                        </FormField>
                        <FormControlLabel
                            control={<Switch size="small" checked={!!accForm.active}
                                onChange={e => setAccForm(f => ({ ...f, active: e.target.checked }))} />}
                            label={<Typography sx={{ fontSize: 12 }}>사용 중 (자금일보에 표시)</Typography>} />
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                            <ActionButton variant="secondary" sx={{ fontSize: 12 }} label="목록으로"
                                onClick={() => { setAccForm(null); setAccEditing(null); }} />
                            <ActionButton variant="add" sx={{ fontSize: 12 }}
                                label={accEditing ? "수정 저장" : "등록"}
                                disabled={!accForm.bankName || !accForm.alias}
                                onClick={saveAccount} />
                        </Box>
                    </Stack>
                )}
            </FormModal>

            {/* 대출 등록/수정 */}
            <FormModal open={loanOpen} onClose={() => setLoanOpen(false)}
                title={loanEditing ? "대출 수정" : "대출 등록"}
                subtitle="상환·대환 시 잔액을 수정하고, 종료된 대출은 '실행 중'을 꺼 주세요."
                maxWidth="sm" onSave={saveLoan}
                saveDisabled={!loanForm.lender || !loanForm.title || loanForm.balance === ""}>
                <Stack spacing={2}>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <FormField label="대출 기관" required>
                            <TextField size="small" value={loanForm.lender}
                                onChange={e => setLoanForm(f => ({ ...f, lender: e.target.value }))}
                                placeholder="예: 농협은행" />
                        </FormField>
                        <FormField label="대출명(표시명)" required>
                            <TextField size="small" value={loanForm.title}
                                onChange={e => setLoanForm(f => ({ ...f, title: e.target.value }))}
                                placeholder="예: 농협_대출계좌(71)" />
                        </FormField>
                    </Box>
                    <FormField label="대출 잔액" required>
                        <TextField size="small" value={loanBalanceDisplay}
                            onChange={e => handleAmountInput(e.target.value, setLoanForm, "balance", setLoanBalanceDisplay)}
                            InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                            inputProps={{ inputMode: "numeric" }} placeholder="0" />
                    </FormField>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <FormField label="연 이자율(%)">
                            <TextField size="small" type="number" value={loanForm.interestRate}
                                onChange={e => setLoanForm(f => ({ ...f, interestRate: e.target.value }))}
                                inputProps={{ step: 0.01, min: 0 }} />
                        </FormField>
                        <FormField label="매월 납입일">
                            <TextField size="small" type="number" value={loanForm.paymentDay}
                                onChange={e => setLoanForm(f => ({ ...f, paymentDay: e.target.value }))}
                                inputProps={{ min: 1, max: 31 }} />
                        </FormField>
                        <FormField label="만기일">
                            <TextField size="small" type="date" value={loanForm.maturityDate}
                                onChange={e => setLoanForm(f => ({ ...f, maturityDate: e.target.value }))} />
                        </FormField>
                    </Box>
                    <FormField label="비고">
                        <TextField size="small" value={loanForm.note}
                            onChange={e => setLoanForm(f => ({ ...f, note: e.target.value }))}
                            placeholder="예: 매월 26일 결제 / 대환으로 종료 등" />
                    </FormField>
                    <FormControlLabel
                        control={<Switch size="small" checked={!!loanForm.active}
                            onChange={e => setLoanForm(f => ({ ...f, active: e.target.checked }))} />}
                        label={<Typography sx={{ fontSize: 12 }}>실행 중 (자금일보에 표시)</Typography>} />
                </Stack>
            </FormModal>

            {/* 이자 지출등록 — 금리 기준 월 이자를 자동 계산해 채워두고, '등록' 클릭 시 지출 예정으로 반영 */}
            <FormModal open={interestOpen} onClose={() => setInterestOpen(false)}
                title="이자 지출 등록"
                subtitle="연 이자율 기준 월 이자(잔액 × 연% ÷ 12)를 자동 계산했습니다. 금액·예정일을 확인하고 등록하세요."
                maxWidth="sm" onSave={submitInterest} saving={busy} saveLabel="지출 예정 등록">
                <Stack spacing={1.5}>
                    {interestItems.map(it => (
                        <Box key={it.loanId} sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, p: 1.5 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap>{it.title}</Typography>
                                <Chip label={`연 ${it.rate}% · 잔액 ${fmtKRWFull(it.balance)}`} size="small"
                                    sx={{ fontSize: 11, height: 20, bgcolor: "#f5f3ff", color: "#7c3aed" }} />
                            </Box>
                            <Box sx={{ display: "flex", gap: 1.5 }}>
                                <FormField label="월 이자(지출)" sx={{ flex: 1, minWidth: 0 }}>
                                    <TextField size="small" fullWidth value={it.amountDisplay}
                                        onChange={e => {
                                            const { display } = toNumericInputState(e.target.value, { emptyZero: true });
                                            setInterestField(it.loanId, "amountDisplay", display);
                                        }}
                                        InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                                        inputProps={{ inputMode: "numeric" }} />
                                </FormField>
                                <FormField label={it.paymentDay ? `지출 예정일 (납입일 매월 ${it.paymentDay}일 기준)` : "지출 예정일 (납입일 미설정 → 오늘)"}
                                    sx={{ flex: 1, minWidth: 0 }}>
                                    <TextField size="small" fullWidth type="date" value={it.scheduledDate}
                                        onChange={e => setInterestField(it.loanId, "scheduledDate", e.target.value)} />
                                </FormField>
                            </Box>
                        </Box>
                    ))}
                </Stack>
            </FormModal>
        </Box>
    );
}
